import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import { DocumentForm, DocumentHeader, DocumentList } from './documents';
import {
  Plus,
  Trash2,
  Eye,
  Download,
  FileText,
  CloudUpload,
  Folder,
  Search,
  Filter,
  Edit,
  Calendar,
  User
} from 'lucide-react';

const DocumentsTab = ({ onDataChange }) => {
  // États de base
  const [documents, setDocuments] = useState([]);
  const [soutenances, setSoutenances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    type_document: '',
    soutenance: ''
  });

  // États pour les modals
  const [showModal, setShowModal] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // États pour les données sélectionnées
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // États pour le formulaire
  const [formData, setFormData] = useState({
    soutenance: '',
    type_document: 'MEMOIRE',
    titre: '',
    fichier: null
  });
  const [formErrors, setFormErrors] = useState({});

  const typesDocuments = [
    { value: 'MEMOIRE', label: 'Mémoire/Thèse', icon: '📖', color: 'bg-blue-100 text-blue-800' },
    { value: 'RESUME', label: 'Résumé', icon: '📄', color: 'bg-green-100 text-green-800' },
    { value: 'RAPPORT_STAGE', label: 'Rapport de stage', icon: '📋', color: 'bg-purple-100 text-purple-800' },
    { value: 'PRESENTATION', label: 'Présentation', icon: '📊', color: 'bg-orange-100 text-orange-800' },
    { value: 'AUTORISATION', label: 'Autorisation', icon: '✅', color: 'bg-green-100 text-green-800' },
    { value: 'PV_SOUTENANCE', label: 'Procès-verbal', icon: '📝', color: 'bg-pink-100 text-pink-800' },
    { value: 'AUTRE', label: 'Autre', icon: '📎', color: 'bg-gray-100 text-gray-800' }
  ];

  // Effet pour nettoyer les états lors du démontage
  useEffect(() => {
    return () => {
      setDocuments([]);
      setLoading(false);
      setSubmitting(false);
      setDeleteLoading(false);
      setError('');
      setSearchTerm('');
      setCurrentPage(1);
      setPageSize(10);
      setPagination(null);
      setFilters({
        type_document: '',
        soutenance: ''
      });
      setShowModal(false);
      setIsConfirmModalOpen(false);
      setSelectedDocument(null);
      setDocumentToDelete(null);
      setConfirmConfig({});
      setFormData({
        soutenance: '',
        type_document: 'MEMOIRE',
        titre: '',
        fichier: null
      });
      setFormErrors({});
      setSoutenances([]);
    };
  }, []);

  // Fonction pour récupérer les documents
  const fetchDocuments = useCallback(async (page = 1, size = 10, search = '', filterParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: size.toString(),
      });

      if (search) {
        params.append('search', search);
      }

      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await api.get(`/soutenance/documents/?${params}`);
      
      if (response.data.status === 'success') {
        setDocuments(response.data.data || []);
        setPagination(response.data.pagination || null);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
        setDocuments([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      setError('Erreur lors du chargement des documents');
      setDocuments([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour récupérer les soutenances
  const fetchSoutenances = useCallback(async () => {
    try {
      const response = await api.get('/soutenance/soutenances/');
      if (response.data.status === 'success') {
        setSoutenances(response.data.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des soutenances:', error);
    }
  }, []);

  // Effet pour charger les données initiales
  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      if (isMounted) {
        await fetchDocuments(currentPage, pageSize, searchTerm, filters);
        await fetchSoutenances();
      }
    };
    
    loadInitialData();
    
    return () => {
      isMounted = false;
    };
  }, [fetchDocuments, fetchSoutenances, currentPage, pageSize, searchTerm, filters]);

  // Gestion des changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouverture du modal avec initialisation des données
  const handleOpenModal = (document = null) => {
    setSelectedDocument(document);
    setFormErrors({});
    
    if (document) {
      setFormData({
        soutenance: document.soutenance?.id || '',
        type_document: document.type_document || 'MEMOIRE',
        titre: document.titre || '',
        fichier: null
      });
    } else {
      setFormData({
        soutenance: '',
        type_document: 'MEMOIRE',
        titre: '',
        fichier: null
      });
    }
    
    setShowModal(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.soutenance) {
      errors.soutenance = 'La soutenance est requise';
    }
    
    if (!formData.titre?.trim()) {
      errors.titre = 'Le titre du document est requis';
    }
    
    if (!formData.type_document) {
      errors.type_document = 'Le type de document est requis';
    }
    
    if (!selectedDocument && !formData.fichier) {
      errors.fichier = 'Le fichier est requis';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
    e.preventDefault();
    }
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('type_document', formData.type_document);
      formDataToSend.append('titre', formData.titre.trim());
      if (formData.fichier) {
        formDataToSend.append('fichier', formData.fichier);
      }

      if (selectedDocument) {
        // Modification
        await api.put(`/soutenance/documents/${selectedDocument.id}/`, formDataToSend);
        toast.success('Document modifié avec succès');
      } else {
        // Création
        await api.post(`/soutenance/soutenances/${formData.soutenance}/documents/`, formDataToSend);
        toast.success('Document uploadé avec succès');
      }

      setShowModal(false);
      await fetchDocuments(currentPage, pageSize, searchTerm, filters);
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Préparation de la suppression
  const handlePrepareDelete = (document) => {
    setDocumentToDelete(document);
    setConfirmConfig({
      title: 'Supprimer le document',
      message: `Êtes-vous sûr de vouloir supprimer le document "${document.titre}" ?`,
      confirmText: 'Supprimer',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  // Suppression
  const handleDelete = async () => {
    if (!documentToDelete) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/soutenance/documents/${documentToDelete.id}/`);
      toast.success('Document supprimé avec succès');
      
      setIsConfirmModalOpen(false);
      setDocumentToDelete(null);
      await fetchDocuments(currentPage, pageSize, searchTerm, filters);
          onDataChange && onDataChange();
      } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Gestion de la pagination
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTypeInfo = (type) => {
    return typesDocuments.find(t => t.value === type) || typesDocuments[typesDocuments.length - 1];
  };

  // Rendu du header
  const renderHeader = () => (
    <DocumentHeader
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onAddNew={() => handleOpenModal()}
      filters={filters}
      onFilterChange={setFilters}
      typesDocuments={typesDocuments}
      soutenances={soutenances}
    />
  );

  // Rendu de la liste des documents
  const renderDocumentsList = () => (
    <DocumentList
      documents={documents}
      loading={loading}
      onEdit={handleOpenModal}
      onDelete={handlePrepareDelete}
      getTypeInfo={getTypeInfo}
    />
  );

  // Rendu du modal
  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      title={selectedDocument ? 'Modifier le Document' : 'Uploader un Document'}
      maxWidth="max-w-2xl"
    >
      <DocumentForm
        formData={formData}
        formErrors={formErrors}
        selectedDocument={selectedDocument}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
        submitting={submitting}
        soutenances={soutenances}
        typesDocuments={typesDocuments}
      />
    </Modal>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
        {renderHeader()}

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Liste des documents */}
        {renderDocumentsList()}

        {/* Pagination */}
        {pagination && documents.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
                      </div>
                    )}

        {/* Modal */}
        {renderModal()}

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
                  />
                </div>
    </div>
  );
};

export default DocumentsTab;
